import _ from '@/underscore'
import $ from '@/jquery'
import ox from '@/ox'
import capabilities from '@/io.ox/core/capabilities'
import { settings as coreSettings } from '@/io.ox/core/settings'
import { addReadyListener, triggerReady } from '@/io.ox/core/events'
import { deriveCheckoutUrl, getCurrentLanguage } from '../upsell/util'
import { hasFeature } from '@/io.ox/core/feature'

export class UpsellContext {
  features = {}
  enabledForUpsell = {}
  #planId = ''
  #ignoreEnabledForUpsell = false
  #missing = new Set()

  // features contains features AND capabilities
  // we just need to ensure that we never name things identically
  constructor (features = {}, enabledForUpsell = {}, planId = 'default') {
    this.features = Object.assign({}, features)
    this.enabledForUpsell = Object.assign({}, enabledForUpsell)
    this.#planId = planId
  }

  #has (id = '') {
    return this.features[id] ?? (hasFeature(id) || !!capabilities.get(id))
  }

  has (id = '') {
    const result = this.#has(id)
    if (!result) this.#missing.add(id)
    return result
  }

  lacks (id = '') {
    return !this.#has(id)
  }

  #enabled (id = '') {
    const result = this.enabledForUpsell[id] ?? false
    if (!result) this.#missing.add(id)
    return result
  }

  enabled (id = '') {
    return !this.#ignoreEnabledForUpsell && this.#enabled(id)
  }

  upsell (id = '') {
    // here it is !has() instead of lacks() to get an entry for missing
    // we also use #enabled as this is an explicit check for upsell
    return !this.has(id) && this.#enabled(id)
  }

  available (id = '') {
    return this.has(id) || this.enabled(id)
  }

  // synonym for available
  visible (id = '') {
    return this.available(id)
  }

  plan (planId = '') {
    return this.#planId === planId
  }

  setPlanId (planId = 'default') {
    this.#planId = planId
  }

  ignoreEnabledForUpsell (state = true) {
    this.#ignoreEnabledForUpsell = state
  }

  getMissing () {
    return [...this.#missing]
  }

  toString () {
    return `${JSON.stringify(this.features)}, ${JSON.stringify(this.enabledForUpsell)}, ${this.#planId}`
  }

  clone (includeEnabledForUpsell = true) {
    return new UpsellContext(this.features, includeEnabledForUpsell ? this.enabledForUpsell : {}, this.#planId)
  }
}

export class Upsell {
  context = null
  #isGuest = false
  #cache = {}

  constructor ({ features = {}, enabledForUpsell = {} } = {}) {
    this.context = new UpsellContext(features, enabledForUpsell)
    this.#isGuest = this.context.has('guest')
  }

  setPlans (plans = {}) {
    // determine plan for the current user (we only need to do this once)
    for (const [id, plan] of Object.entries(plans)) {
      if (plan?.condition && this.has(plan.condition)) {
        if (ox.debug) console.debug('Upsell: User matches plan', id, plan)
        this.context.setPlanId(id)
        this.resetCache()
        break
      }
    }
  }

  has (...args) {
    const condition = this.#getConditionFromArgs(args, ' || ')
    if (!condition) return true
    const result = this.evaluate(condition)
    if (result.empty) return true
    return result.has
  }

  available (...args) {
    const condition = this.#getConditionFromArgs(args, ' || ')
    if (!condition) return true
    const result = this.evaluate(condition)
    return result.has || result.available
  }

  missing (...args) {
    const condition = this.#getConditionFromArgs(args, ' ')
    if (!condition) return ''
    const result = this.evaluate(condition)
    return result.missing.join(' ')
  }

  wouldTriggerUpsell (...args) {
    const condition = this.#getConditionFromArgs(args, ' || ')
    if (!condition) return false
    if (this.#isGuest) return false
    const result = this.evaluate(condition)
    if (result.empty) return false
    return !result.has && result.available
  }

  // example: 'a', 'b || c' -> 'a || b || c'
  // example: 'a b && c' -> 'a && b && c'
  // example: 'a,b' -> invalid
  enabled (...args) {
    // actually, we the most recent changes, enabled works the very same way as visible
    if (ox.debug) console.warn('upsell.enabled(...) is deprecated with 8.41! Please use !has(...) && available(...) or isEnabled() for check a single capability.', args)
    return this.available(...args)
  }

  // checks if something should be visible depending on required capabilities
  // true if any item matches requires capabilities
  // true if any item does not match its requirements but is enabled for upsell
  // this function is used for any inline link, for example, to decide whether or not showing it
  visible (...args) {
    return this.available(...args)
  }

  #getConditionFromArgs (args = [], separator = ' || ') {
    args = args.flat().filter(Boolean)
    const condition = args.length > 1
      ? args.map(str => `(${String(str).trim()})`).join(separator)
      : String(args[0] || '').trim()
    if (ox.debug && /,/.test(condition)) {
      console.error('You must not use commas in conditions. Use spaces instead!', condition)
      return ''
    }
    return condition
  }

  // checks condition for upsell triggers
  // input is string; expression gets evaluated
  // - false for guests
  // - false if all capabilities are given
  // - true IF at least one capability is missing BUT enabled for upsell
  shouldShowUpsellTrigger (condition = '') {
    if (this.#isGuest) return false
    const result = this.evaluate(condition, { shorthand: 'upsell' })
    if (result.empty) return false
    return result.available
  }

  missingInUpsellTrigger (condition = '') {
    if (!condition) return ''
    const result = this.evaluate(condition, { shorthand: 'upsell' })
    return result.missing
  }

  evaluate (expression = '', { shorthand = 'available' } = {}) {
    const key = `${expression}(shorthand=${shorthand}; ${this.context})`
    if (key in this.#cache) return this.#cache[key]
    return (this.#cache[key] = this.#evaluate(expression, { shorthand }))
  }

  #evaluate (condition = '', { shorthand = 'available' } = {}) {
    // remove any unexpected characters
    condition = String(condition).trim().replace(capabilities.allowlistRegex, '')
    // empty?
    if (!condition) return { has: true, available: true, missing: [], empty: true, condition, expression: '' }
    // support for shorthands
    condition = condition.replace(/([^&|]) ([^&|])/gi, '$1 && $2')
    // allow function calls (for atomic checks)
    let ctx = this.context.clone()
    const methods = this.#getAllMethodNames(ctx)
    condition = methods.reduce((str, name) => str.replace(new RegExp(`${name}\\((.*?)\\)`, 'g'), `ctx.${name}("$1")`), condition)
    // apply regex
    const expression = condition.replace(/(['"]?\w[\w:\-./]*['"]?)/ig, match => {
      if (/^['"].+['"]$/.test(match)) return match
      if (/^ctx\./.test(match)) return match
      return `ctx.${shorthand}("${match}")`
    })
    try {
      /* eslint no-new-func: 0 */
      const fn = new Function('ctx', `return !!(${expression})`)
      ctx.ignoreEnabledForUpsell()
      const withoutUpsell = fn(ctx)
      const missing = ctx.getMissing()
      if (withoutUpsell) return { has: true, available: true, missing, condition, expression }
      if (!this.#isGuest) {
        ctx = this.context.clone()
        const withUpsell = fn(ctx)
        if (withUpsell) return { has: false, available: true, missing: ctx.getMissing(), condition, expression }
      }
      return { has: false, available: false, missing, condition, expression }
    } catch (e) {
      console.error('upsell.evaluate()', expression, e)
      return { has: false, available: false, missing: [], condition, expression }
    }
  }

  #getAllMethodNames (obj) {
    const names = Object.getOwnPropertyNames(obj)
    const prototype = Object.getPrototypeOf(obj)
    if (prototype !== Object.prototype) names.push(...Object.getOwnPropertyNames(prototype))
    return names.filter(name => name !== 'constructor' && typeof obj[name] === 'function')
  }

  resetCache () {
    this.#cache = {}
  }

  // helpful if something goes wrong
  debug () {
    console.debug('enabledForUpsell', this.context.enabledForUpsell, 'cache', this.#cache)
  }
}

const that = {}

const instance = new Upsell()
const methods = ['has', 'missing', 'available', 'visible', 'enabled', 'plan', 'wouldTriggerUpsell', 'shouldShowUpsellTrigger', 'missingInUpsellTrigger', 'debug']
methods.forEach(method => {
  that[method] = (...args) => instance[method](...args)
})

Object.assign(that, {
  // find one set of capabilities that matches
  // returns true or false
  any (array) {
    if (ox.debug) console.warn('upsell.any() is deprecated with 8.41 (due to low or no usage)')
    if (!array) return true
    return _([].concat(array)).reduce(function (memo, c) {
      return memo || c === undefined || that.has(c)
    }, false)
  },

  // trigger upsell. options should contain at least:
  // - type: 'app' or 'custom'
  // - id: app id or trigger id
  // - missing: comma-separated list of missing capabilities
  trigger ({ type = 'custom', id = 'unknown', missing = '', ...args } = {}) {
    ox.trigger('upsell:requires-upgrade', { type, id, missing, ...args })
  },

  // simple click handler
  click (e) {
    e.preventDefault()
    that.trigger()
  },

  captureRequiresUpgrade () {
    ox.on('upsell:requires-upgrade', async (options) => {
      const { showPlans } = await import('@/pe/upsell/checkout/plans')
      showPlans(options)
    })
    that.captureRequiresUpgrade = $.noop
  },

  captureUpgrade () {
    ox.on('upsell:upgrade', upgrade)
    that.captureUpgrade = $.noop
  },

  useDefaults () {
    that.captureRequiresUpgrade()
    that.captureUpgrade()
  },

  getTriggerConfig: _.memoize(
    (id = '', { enabled = true } = {}) => {
      // DEPRECATED: Setting `io.ox/core//features/upsell/<id>`, pending full removal with 8.47. Please use `io.ox/core//upsell/triggers/<id>` instead.
      const notMigratedYet = !!coreSettings.get(`features/upsell/${id}`) && !coreSettings.get(`upsell/triggers/${id}`)
      if (notMigratedYet) console.error('Setting `io.ox/core//features/upsell/<id>` is deprecated, pending full removal with 8.47. Please use `io.ox/core//upsell/triggers/<id>` instead.', id)

      // support new and old location
      const config = Object.assign({ enabled }, coreSettings.get(`features/upsell/${id}`), coreSettings.get(`upsell/triggers/${id}`))
      if (config.i18n) {
        const locale = ox.language
        const language = getCurrentLanguage()
        // locale (more specific) wins over language
        if (config.i18n[locale]) Object.assign(config, config.i18n[locale])
        else if (config.i18n[language]) Object.assign(config, config.i18n[language])
      }
      // allows debugging/changing this configuration on code-level
      ox.trigger('upsell:trigger-config', { id, config })
      return config
    },
    (id = '', { enabled = true } = {}) => `${id}/${enabled}`
  ),

  getDefaultIcon () {
    return coreSettings.get('upsell/defaultIcon', 'bi/stars.svg')
  },

  // just for demo purposes
  // flag helps during development of custom upsell wizard; just disables some capabilities but
  // neither registers events nor adds portal plugin
  demo (debugCustomWizard) {
    // all features enabled for upsell
    Object.assign(instance.context.enabledForUpsell, {
      active_sync: false,
      'ai-service': true,
      calendar: true,
      caldav: false,
      carddav: true,
      contacts: true,
      guard: true,
      infostore: true,
      portal: true,
      webmail: true,
      // enable storage/quota upgrades
      storage: true
    })
    // disable some capabilities to show triggers
    Object.assign(instance.context.features, {
      active_sync: false,
      'ai-service': false,
      calendar: false,
      caldav: false,
      carddav: false,
      extended_quota: false,
      guard: false,
      infostore: false
    })
    instance.resetCache()

    coreSettings.set('upsell/activated', true)
    coreSettings.set('upsell/triggers/upgrade-widget/apps', ['mail'])
    coreSettings.set('upsell/triggers/portal-widget', { enabled: true, requires: 'infostore' })
    coreSettings.set('upsell/triggers/upgrade-button', { enabled: true, requires: 'infostore' })
    coreSettings.set('upsell/triggers/folderview/mail', { enabled: false })
    coreSettings.set('upsell/triggers/topbar-dropdown', { color: '#ff0000' })
    coreSettings.set('features/upsell/client.onboarding', { enabled: true })
    coreSettings.set('enabled/ai', true)
    console.debug('Running Upsell Demo')
    if (!debugCustomWizard) {
      that.useDefaults()
    }
  },

  // for development & debugging
  setEnabled (id) {
    instance.context.enabledForUpsell[id] = true
    instance.resetCache()
  },

  isEnabled (id) {
    return !!instance.context.enabledForUpsell[id]
  },

  isActivated () {
    // killswitch for upsell, needed to enable/disable the feature without changing the whole config
    // needed a different term than "enabled" as it's already taken
    const activated = coreSettings.get('upsell/activated', false)
    if (!activated) return false
    // upsell is enabled in general -- now look at device-specific switches
    if (_.device('smartphone')) return coreSettings.get('upsell/activated-on-smartphone', true)
    return true
  }
})

function upgrade (context) {
  if (ox.debug) console.debug('upsell:upgrade', context)
  const url = deriveCheckoutUrl(context)
  window.open(url, 'checkout')
}

addReadyListener('settings', () => {
  const activated = that.isActivated()
  instance.context.enabledForUpsell = activated ? coreSettings.get('upsell/enabled') || {} : {}

  // plans (object): key (string) is planId; value (object) must at least contain a "condition"
  const plans = coreSettings.get('upsell/plans')
  if (plans) instance.setPlans(plans)

  triggerReady('upsell')

  // demo mode
  const hash = _.url.hash('demo') || ''
  // allow upsell demo also via setting in addition to hash "demo=upsell"
  const demoMode = hash.indexOf('upsell') > -1 || coreSettings.get('upsell/demo', false)

  if (demoMode) that.demo()
  // stop here if not activated
  else if (!activated) return

  // stop if no checkout is defined
  const type = coreSettings.get('upsell/checkout/type')
  if (!type || type === 'none') return

  ox.off('upsell:requires-upgrade').on('upsell:requires-upgrade', async (context) => {
    // get checkout type (again) to allow changing this on-the-fly
    const type = coreSettings.get('upsell/checkout/type')
    context.checkoutType = type
    switch (type) {
      case 'tab':
        ox.trigger('upsell:upgrade', context)
        break
      case 'modal': {
        const { showUpgradeDialog } = await import('@/pe/upsell/checkout/modal')
        showUpgradeDialog(context)
        break
      }
      case 'iframe': {
        const { default: wizard } = await import('@/pe/upsell/plugins/simple-wizard/register')
        const url = deriveCheckoutUrl(context)
        wizard.open(context, url)
        break
      }
      case 'plans': {
        const { showPlans } = await import('@/pe/upsell/checkout/plans')
        showPlans(context)
        break
      }
      default:
        console.warn('No valid checkout type configured', type)
        break
    }
  })

  ox.off('upsell:upgrade').on('upsell:upgrade', upgrade)
})

if (ox.debug) window.upsell = that

export default that
